import { FC, Fragment, useCallback, useEffect, useState } from 'react'
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import EditCalendarIcon from '@mui/icons-material/EditCalendar'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import {
  fetchSiteItemRates,
  SiteRateCategoryGroup,
} from '../../../services'
import { InlineEditableField } from '../../../components'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import { showAlertAction } from '../../../store/alerts'
import { formatSlipRate } from '../../../utils'
import EditSiteItemRate, { type EditSiteItemRateTarget } from './EditSiteItemRate'

/** Same as AllItemComponent / OutItemComponent / InItemComponent column header strip */
const SLIPS_TABLE_HEADER_BG = '#f9f9f9'

const slipRateParser = (val: string) =>
  parseFloat(val.replace(/[^0-9.]/g, ''))
const hasText = (v?: string | null) => typeof v === 'string' && v.trim() !== ''

export interface SiteRatesTabProps {
  siteId: number
  /** Called after a successful item or category rate save (parent may mark slips refresh). */
  onRatesUpdated?: () => void
}

export const SiteRatesTab: FC<SiteRatesTabProps> = ({ siteId, onRatesUpdated }) => {
  const theme = useTheme()
  const dispatchAction = useAppDispatch()
  const slipsListLoading = useAppSelector((state) => state.manageSlips.loading)
  const [loading, setLoading] = useState(true)
  const [groups, setGroups] = useState<SiteRateCategoryGroup[]>([])
  const [editingTarget, setEditingTarget] = useState<EditSiteItemRateTarget | null>(
    null
  )

  const load = useCallback(
    (silent?: boolean) => {
      if (!siteId || Number.isNaN(siteId)) {
        setLoading(false)
        setGroups([])
        return
      }
      if (!silent) setLoading(true)
      fetchSiteItemRates(siteId)
        .then((res) => {
          if (res.success && Array.isArray(res.data)) {
            setGroups(res.data)
          } else {
            setGroups([])
            if (res.message) {
              dispatchAction(
                showAlertAction({ message: res.message, severity: 'error' })
              )
            }
          }
        })
        .catch(() => {
          setGroups([])
          dispatchAction(
            showAlertAction({
              message: 'Failed to load site rates',
              severity: 'error',
            })
          )
        })
        .finally(() => {
          if (!silent) setLoading(false)
        })
    },
    [siteId, dispatchAction]
  )

  useEffect(() => {
    load(false)
  }, [load])

  const onRateSaveSuccess = useCallback(() => {
    onRatesUpdated?.()
    load(true)
  }, [load, onRatesUpdated])

  if (loading) {
    if (slipsListLoading) {
      return null
    }
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!groups.length) {
    return (
      <Box sx={{ py: 4, px: 2 }}>
        <Typography color="text.secondary">No rate categories for this site.</Typography>
      </Box>
    )
  }

  return (
    <>
      <Divider />
      <TableContainer sx={{ width: '100%', mt: 0 }}>
        <Table
          size="small"
          aria-label="Site item rates"
          sx={{
            width: '100%',
            tableLayout: 'fixed',
            '& .MuiTableRow-head': {
              backgroundColor: `${SLIPS_TABLE_HEADER_BG} !important`,
            },
            '& .MuiTableCell-head': {
              color: 'text.primary',
              backgroundColor: `${SLIPS_TABLE_HEADER_BG} !important`,
            },
          }}
        >
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: SLIPS_TABLE_HEADER_BG,
                minHeight: '4vh',
                position: 'sticky',
                top: 0,
                zIndex: 997,
                '& th': { borderBottom: 1, borderColor: 'divider' },
              }}
            >
              <TableCell
                component="th"
                scope="col"
                sx={{
                  fontWeight: 'bold',
                  width: '46%',
                  pl: 4,
                  pr: 2,
                  py: 1.25,
                  backgroundColor: SLIPS_TABLE_HEADER_BG,
                  color: 'text.primary',
                }}
              >
                Name
              </TableCell>
              <TableCell
                component="th"
                scope="col"
                align="right"
                sx={{
                  fontWeight: 'bold',
                  width: '38%',
                  pl: 2,
                  pr: 3,
                  py: 1.25,
                  backgroundColor: SLIPS_TABLE_HEADER_BG,
                  color: 'text.primary',
                }}
              >
                <Typography sx={{ mr: 4 }}>Rate</Typography>
              </TableCell>
              <TableCell
                component="th"
                scope="col"
                align="center"
                sx={{
                  fontWeight: 'bold',
                  width: '16%',
                  py: 1.25,
                  backgroundColor: SLIPS_TABLE_HEADER_BG,
                  color: 'text.primary',
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((cat) => (
              <Fragment key={cat.categoryId}>
                <TableRow
                  sx={{
                    backgroundColor: SLIPS_TABLE_HEADER_BG,
                    '& td': { borderBottom: 1, borderColor: 'divider' },
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      pl: 4,
                      pr: 2,
                      backgroundColor: SLIPS_TABLE_HEADER_BG,
                      color: 'text.primary',
                    }}
                  >
                    {cat.name}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      pl: 2,
                      pr: 3,
                      verticalAlign: 'middle',
                      backgroundColor: SLIPS_TABLE_HEADER_BG,
                      color: 'text.primary',
                    }}
                  >
                    {cat.isInlineEditable === false ? (
                      <Typography variant="body2">
                        {cat.rate == null ? '-' : formatSlipRate(cat.rate)}
                      </Typography>
                    ) : (
                      <Box sx={{ m: 0.2, height: 30 }}>
                        <InlineEditableField
                          value={cat.rate}
                          fieldName="rate"
                          entryId={cat.categoryId}
                          entity="site_category_rate"
                          siteId={siteId}
                          formatter={formatSlipRate}
                          parser={slipRateParser}
                          type="number"
                          min={-0.00001}
                          onUpdateSuccess={onRateSaveSuccess}
                        />
                      </Box>
                    )}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      backgroundColor: SLIPS_TABLE_HEADER_BG,
                      borderBottom: 1,
                      borderColor: 'divider',
                      verticalAlign: 'middle',
                    }}
                  >
                    <Tooltip title="Edit category rate / cycle plan" arrow>
                      <IconButton
                        size="small"
                        aria-label="Edit site category rate"
                        data-cy={`edit_site_category_rate_${cat.categoryId}`}
                        onClick={() =>
                          setEditingTarget({ kind: 'category', category: cat })
                        }
                        sx={{
                          '&:hover': {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <EditCalendarIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
                {cat.items.map((item) => (
                  <TableRow key={`item-${item.itemId}`} hover>
                    <TableCell sx={{ pl: 4, borderBottom: 1, borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">{item.itemName}</Typography>
                        {item.warning ? (
                          <Tooltip title={item.warning} arrow>
                            <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                          </Tooltip>
                        ) : null}
                      </Box>
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        pl: 2,
                        pr: 3,
                        borderBottom: 1,
                        borderColor: 'divider',
                        verticalAlign: 'middle',
                      }}
                    >
                      {hasText(item.cycleRates) ? (
                        <Typography variant="body2">{item.cycleRates}</Typography>
                      ) : (
                        <Box sx={{ m: 0.2, height: 30 }}>
                          <InlineEditableField
                            value={item.rate}
                            fieldName="rate"
                            entryId={item.itemId}
                            entity="site_item_rate"
                            siteId={siteId}
                            formatter={formatSlipRate}
                            parser={slipRateParser}
                            type="number"
                            min={-0.00001}
                            onUpdateSuccess={onRateSaveSuccess}
                          />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        verticalAlign: 'middle',
                      }}
                    >
                      <Tooltip title="Edit rate / cycle plan" arrow>
                        <IconButton
                          size="small"
                          aria-label="Edit site item rate"
                          data-cy={`edit_site_item_rate_${item.itemId}`}
                          onClick={() =>
                            setEditingTarget({ kind: 'item', row: item })
                          }
                          sx={{
                            '&:hover': {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                        >
                          <EditCalendarIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {editingTarget ? (
        <EditSiteItemRate
          siteId={siteId}
          target={editingTarget}
          onClose={() => setEditingTarget(null)}
          onSuccess={() => {
            onRatesUpdated?.()
            load(true)
          }}
        />
      ) : null}
    </>
  )
}

export default SiteRatesTab
